import React from "react";
import {useCurrentFrame, useVideoConfig, spring, interpolate} from "remotion";
import type {QuizQuestion} from "./types";
import {TimerBar} from "./TimerBar";
import {OptionPill} from "./OptionPill";
import {Confetti} from "../../../components/dimus/Confetti";
import {
  TEXT_PRIMARY,
  CATEGORY_COLOR,
  WORD_GAP,
  BUTTON_ENTER_START,
  BUTTON_STAGGER,
  REVEAL_FRAME,
  POST_REVEAL_WAIT,
  EXIT_DURATION,
  EXIT_STAGGER,
  CLAMP,
  FONT_STACK,
} from "./constants";

type PillState = "default" | "correct" | "wrong";

export const QuestionScene: React.FC<{
  question: QuizQuestion;
  index: number;
}> = ({question, index}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const timerProgress = interpolate(frame, [0, REVEAL_FRAME], [0, 1], CLAMP);

  const getPillState = (optionIndex: number): PillState => {
    if (frame < REVEAL_FRAME) return "default";
    if (optionIndex === question.correctIndex) return "correct";
    return "wrong";
  };

  // Cascade exit
  const exitStart = REVEAL_FRAME + POST_REVEAL_WAIT;
  const elementIds = [
    "timer",
    "category",
    "question",
    ...question.options.map((_, i) => `option-${i}`),
  ];
  const exitOrder = [...elementIds].reverse();

  const getExitStyle = (elementId: string): React.CSSProperties => {
    const exitIndex = exitOrder.indexOf(elementId);
    const elExitStart = exitStart + exitIndex * EXIT_STAGGER;

    const exitProgress = interpolate(
      frame,
      [elExitStart, elExitStart + EXIT_DURATION],
      [0, 1],
      {...CLAMP, easing: (t: number) => t * t * t}
    );

    const translateY = interpolate(exitProgress, [0, 1], [0, -60], CLAMP);
    const opacity = interpolate(
      frame,
      [elExitStart, elExitStart + 10],
      [1, 0],
      CLAMP
    );

    return {opacity, transform: `translateY(${translateY}px)`};
  };

  const words = question.text.split(" ");

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "0 64px",
        gap: 16,
      }}
    >
      <div style={getExitStyle("timer")}>
        <TimerBar progress={timerProgress} />
      </div>

      <div style={getExitStyle("category")}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: CATEGORY_COLOR,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            fontFamily: FONT_STACK,
          }}
        >
          {question.category}
        </span>
      </div>

      <div
        style={{
          ...getExitStyle("question"),
          display: "flex",
          flexWrap: "wrap",
          gap: 0,
          maxWidth: "92%",
        }}
      >
        {words.map((word, i) => {
          const wordStart = i * WORD_GAP;
          const progress = spring({
            frame: frame - wordStart,
            fps,
            config: {damping: 14, stiffness: 120, mass: 0.8},
          });
          const wordY = interpolate(progress, [0, 1], [12, 0], CLAMP);

          return (
            <span
              key={`q${index}-w${i}`}
              style={{
                opacity: progress,
                transform: `translateY(${wordY}px)`,
                display: "inline-block",
                marginRight: 8,
                fontSize: 32,
                fontWeight: 700,
                color: TEXT_PRIMARY,
                fontFamily: FONT_STACK,
                lineHeight: 1.3,
              }}
            >
              {word}
            </span>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginTop: 12,
          position: "relative",
        }}
      >
        {question.options.map((opt, i) => {
          const enterDelay = BUTTON_ENTER_START + i * BUTTON_STAGGER;
          return (
            <div key={`q${index}-o${i}`} style={getExitStyle(`option-${i}`)}>
              <OptionPill
                text={opt}
                index={i}
                state={getPillState(i)}
                enterDelay={enterDelay}
              />
            </div>
          );
        })}

        {frame >= REVEAL_FRAME && (
          <div
            style={{
              position: "absolute",
              left: 120,
              top: question.correctIndex * 54 + 22,
              pointerEvents: "none",
            }}
          >
            <Confetti triggerFrame={REVEAL_FRAME} />
          </div>
        )}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
